import { useEffect, useState } from "react";
import { ArrowRight, Check, Globe, MapPin, List, FileText, Mail } from "lucide-react";
import AppShell from "@/components/AppShell";

/**
 * The Welcome-to-Monday-Brief experience.
 *
 * Design intent: calm confidence, not celebration.
 * The customer should leave this page thinking "these people understand my business",
 * not "I finished setting up another app".
 *
 * Future extension: the "Behind the scenes" section reserves a slot for small
 * insight updates (e.g. "We found your Google Business Profile") that can be
 * dropped in later without redesigning the page. Pass them via `insights` prop
 * when the real monitoring pipeline is wired up.
 */

const CHECKLIST = [
  { id: "gbp", label: "Reviewing your Google Business Profile", icon: MapPin },
  { id: "site", label: "Checking your website", icon: Globe },
  { id: "listings", label: "Reviewing your online business listings", icon: List },
  { id: "reach", label: "Checking how customers can find and contact you", icon: Mail },
  { id: "brief", label: "Preparing your first Monday Brief", icon: FileText },
];

export default function WelcomeExperience({ onContinue, insights = [] }) {
  const [checked, setChecked] = useState(0);

  useEffect(() => {
    if (checked >= CHECKLIST.length) return;
    const t = setTimeout(() => setChecked((c) => c + 1), checked === 0 ? 500 : 950);
    return () => clearTimeout(t);
  }, [checked]);

  return (
    <AppShell testId="onboarding-complete" showTopBar>
      <div className="pt-6 sm:pt-10 max-w-lg mx-auto page-fade" data-testid="welcome-experience">
        {/* Eyebrow — no confetti, just a calm marker */}
        <div className="text-xs uppercase tracking-widest font-semibold mb-3" style={{ color: "var(--ta-secondary)" }}>
          Setup complete
        </div>

        {/* Headline */}
        <h1
          className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.08]"
          style={{ color: "var(--ta-primary)" }}
          data-testid="completion-headline"
        >
          You're all set. We've already started building your first Monday Brief.
        </h1>

        {/* Supporting copy — written like an advisor, not software */}
        <div className="mt-5 space-y-3 text-base sm:text-lg leading-relaxed" style={{ color: "var(--ta-text-secondary)" }}>
          <p>Over the next few minutes we'll begin learning about your business.</p>
          <p>We'll quietly review the places your customers already look.</p>
          <p>By next Monday, you'll receive one clear brief showing what matters most and what you should do next.</p>
          <p className="pt-1" style={{ color: "var(--ta-primary)" }}>
            Until then <span aria-hidden>—</span> <span className="font-medium">we've got this.</span>
          </p>
        </div>

        {/* Behind the scenes: the reassurance checklist */}
        <section
          className="mt-10 rounded-[24px] border p-6 sm:p-7"
          style={{ borderColor: "var(--ta-border)", background: "#fff" }}
          data-testid="welcome-checklist"
        >
          <div className="text-xs uppercase tracking-widest font-semibold mb-1" style={{ color: "var(--ta-secondary)" }}>
            Behind the scenes
          </div>
          <div className="font-display text-xl font-semibold mb-5" style={{ color: "var(--ta-primary)" }}>
            What we're doing right now
          </div>

          <ol className="flex flex-col gap-3.5">
            {CHECKLIST.map((item, i) => {
              const state =
                i < checked ? "done" : i === checked ? "active" : "pending";
              return (
                <ChecklistRow key={item.id} item={item} state={state} />
              );
            })}
          </ol>

          {/* Future insights slot — renders progress-update cards when available. */}
          {insights.length > 0 && (
            <div className="mt-6 pt-6 border-t flex flex-col gap-3" style={{ borderColor: "var(--ta-border)" }} data-testid="welcome-insights">
              {insights.map((ins, idx) => (
                <div
                  key={ins.id || idx}
                  className="p-4 rounded-2xl flex items-start gap-3"
                  style={{ background: "var(--ta-success-bg)", color: "var(--ta-text)" }}
                >
                  <div
                    className="w-8 h-8 rounded-full inline-flex items-center justify-center shrink-0"
                    style={{ background: "var(--ta-success)", color: "#fff" }}
                  >
                    <Check size={16} strokeWidth={2.6} />
                  </div>
                  <div>
                    <div className="font-medium" style={{ color: "var(--ta-primary)" }}>{ins.title}</div>
                    {ins.detail && (
                      <div className="text-sm mt-0.5" style={{ color: "var(--ta-text-secondary)" }}>
                        {ins.detail}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* What happens next */}
        <section className="mt-10" data-testid="welcome-what-next">
          <div className="text-xs uppercase tracking-widest font-semibold mb-1" style={{ color: "var(--ta-secondary)" }}>
            What happens next
          </div>
          <h2 className="font-display text-2xl font-semibold" style={{ color: "var(--ta-primary)" }}>
            Your first week with us
          </h2>

          <ol className="mt-5 relative flex flex-col gap-5">
            <NextItem
              when="Today"
              text="We're gathering information about your business."
              testId="what-next-today"
            />
            <NextItem
              when="This week"
              text="We're quietly watching for meaningful insights."
              testId="what-next-this-week"
            />
            <NextItem
              when="Next Monday"
              text="You'll receive your first Monday Brief."
              testId="what-next-next-monday"
              accent
            />
          </ol>
        </section>

        {/* Friendly reminder */}
        <section
          className="mt-10 rounded-[24px] p-6 flex items-start gap-4"
          style={{ background: "var(--ta-accent)", color: "var(--ta-primary)" }}
          data-testid="welcome-reminder"
        >
          <div
            className="w-10 h-10 rounded-xl inline-flex items-center justify-center shrink-0"
            style={{ background: "#fff", color: "var(--ta-primary)" }}
          >
            <Mail size={20} strokeWidth={2.2} />
          </div>
          <div>
            <div className="font-display font-semibold text-base sm:text-lg">
              We'll email your Monday Brief.
            </div>
            <p className="mt-1 text-sm sm:text-base leading-relaxed" style={{ color: "var(--ta-primary-light)" }}>
              No login required. If you'd rather explore before then, you can always return here.
            </p>
          </div>
        </section>

        {/* Primary CTA — inviting, not dismissive */}
        <button
          onClick={onContinue}
          data-testid="completion-cta"
          className="mt-10 w-full inline-flex items-center justify-center gap-2 min-h-[64px] rounded-2xl text-lg font-medium text-white transition-transform hover:-translate-y-0.5"
          style={{ background: "var(--ta-primary)" }}
        >
          Here Is What We Found <ArrowRight size={20} />
        </button>

        <p className="mt-5 mb-2 text-center text-sm" style={{ color: "var(--ta-text-secondary)" }}>
          You can close this tab any time. We'll keep working in the background.
        </p>
      </div>
    </AppShell>
  );
}

/* ---------- pieces ---------- */

const ChecklistRow = ({ item, state }) => {
  const done = state === "done";
  const active = state === "active";
  const Icon = item.icon;

  return (
    <li
      className="flex items-center gap-3 transition-opacity"
      style={{ opacity: state === "pending" ? 0.55 : 1 }}
      data-testid={`welcome-checklist-item-${item.id}`}
      data-state={state}
    >
      <div
        className="relative w-8 h-8 rounded-full inline-flex items-center justify-center shrink-0 transition-colors"
        style={{
          background: done ? "var(--ta-success)" : "var(--ta-surface-alt)",
          color: done ? "#fff" : "var(--ta-text-secondary)",
        }}
      >
        {done ? (
          <Check size={16} strokeWidth={2.8} />
        ) : (
          <Icon size={15} strokeWidth={2.2} />
        )}
        {active && (
          <span
            className="absolute inset-0 rounded-full animate-ping"
            style={{ background: "var(--ta-primary)", opacity: 0.18 }}
            aria-hidden
          />
        )}
      </div>
      <span
        className="text-base sm:text-[17px] leading-snug"
        style={{
          color: done ? "var(--ta-primary)" : "var(--ta-text)",
          fontWeight: done ? 500 : 400,
        }}
      >
        {item.label}
      </span>
    </li>
  );
};

const NextItem = ({ when, text, testId, accent = false }) => (
  <li className="flex gap-4" data-testid={testId}>
    <div
      className="w-2.5 h-2.5 rounded-full mt-2 shrink-0"
      style={{ background: accent ? "var(--ta-secondary)" : "var(--ta-primary)" }}
      aria-hidden
    />
    <div>
      <div
        className="text-xs uppercase tracking-widest font-semibold"
        style={{ color: accent ? "var(--ta-secondary)" : "var(--ta-primary)" }}
      >
        {when}
      </div>
      <div className="mt-1 text-base sm:text-lg leading-relaxed" style={{ color: "var(--ta-text)" }}>
        {text}
      </div>
    </div>
  </li>
);
